import type { CellRange } from './range.js';
import { clampRect, normalizeRange } from './range.js';

export type FillValue = string | number | boolean | null | undefined;
export type FillDirection = 'down' | 'up' | 'left' | 'right';

function isEmpty(v: FillValue): boolean {
  return v === null || v === undefined || v === '';
}

function asNumber(v: FillValue): number | undefined {
  if (typeof v === 'number') return Number.isFinite(v) ? v : undefined;
  if (typeof v === 'string' && /^-?\d+(?:\.\d+)?$/.test(v.trim())) return Number(v);
  return undefined;
}

const SUFFIX = /^(.*?)(\d+)$/;

/** Next value of a series continued from `seeds`, or undefined when the seeds are no series. */
function seriesStep(seeds: readonly FillValue[]): ((i: number) => FillValue) | undefined {
  if (seeds.length < 2) return undefined;
  const nums = seeds.map(asNumber);
  if (nums.every((n) => n !== undefined)) {
    const first = nums[0]!;
    const step = (nums[nums.length - 1]! - first) / (nums.length - 1);
    const asString = typeof seeds[0] === 'string';
    return (i) => {
      const n = Math.round((first + step * i) * 1e9) / 1e9;
      return asString ? String(n) : n;
    };
  }
  const parts = seeds.map((s) => (typeof s === 'string' ? SUFFIX.exec(s) : null));
  if (parts.every((m) => m !== null) && parts.every((m) => m[1] === parts[0]![1])) {
    const first = Number(parts[0]![2]);
    const step = (Number(parts[parts.length - 1]![2]) - first) / (parts.length - 1);
    if (Number.isInteger(step)) return (i) => `${parts[0]![1]}${first + step * i}`;
  }
  return undefined;
}

/**
 * Fills one line (column for up/down, row for left/right) in fill order. The seed is the leading
 * run of non-empty cells; if it spans the whole line only the first cell is the seed (Ctrl+D).
 * Two or more numeric (or `Text<n>`) seeds continue as a linear series, otherwise the seed
 * pattern repeats.
 */
export function fillLine(values: readonly FillValue[]): FillValue[] {
  let seedLength = 0;
  while (seedLength < values.length && !isEmpty(values[seedLength])) seedLength++;
  if (seedLength === values.length || seedLength === 0) seedLength = 1;
  const seeds = values.slice(0, seedLength);
  const series = seriesStep(seeds);
  return values.map((v, i) => {
    if (i < seedLength) return v;
    return series === undefined ? seeds[i % seedLength] : series(i);
  });
}

/**
 * Excel-style fill within `range` (clamped to the grid). `down` copies from the top row, `up` from
 * the bottom, `right` from the left column, `left` from the right. Returns a new grid; cells
 * outside the range are untouched.
 */
export function fill<T extends FillValue>(
  grid: readonly (readonly T[])[],
  range: CellRange,
  direction: FillDirection,
): T[][] {
  const out = grid.map((row) => [...row]);
  const cols = Math.max(0, ...grid.map((r) => r.length));
  const rect = clampRect(normalizeRange(range), grid.length, cols);
  if (rect === undefined) return out;
  const vertical = direction === 'down' || direction === 'up';
  const reverse = direction === 'up' || direction === 'left';
  const [lineStart, lineEnd] = vertical ? [rect.left, rect.right] : [rect.top, rect.bottom];
  const [posStart, posEnd] = vertical ? [rect.top, rect.bottom] : [rect.left, rect.right];
  for (let line = lineStart; line <= lineEnd; line++) {
    const positions: number[] = [];
    for (let p = posStart; p <= posEnd; p++) positions.push(p);
    if (reverse) positions.reverse();
    const cell = (p: number): [number, number] => (vertical ? [p, line] : [line, p]);
    const filled = fillLine(positions.map((p) => out[cell(p)[0]]![cell(p)[1]]));
    positions.forEach((p, i) => {
      const [r, c] = cell(p);
      out[r]![c] = filled[i] as T;
    });
  }
  return out;
}
